import {
    AfterViewInit,
    Component,
    Input,
    OnDestroy,
    OnInit,
    booleanAttribute, numberAttribute,
} from '@angular/core'
import { Availability } from '../domain/Availability'
import { AvailabilityService } from '../services/availability.service'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { adaptStringToDate, Helper } from '../utilities/helper'
import { NGBDateTimeAdapter } from '../utilities/NgbDateTimeAdapter'
import { NgbDateNativeAdapter, NgbDatepickerI18n, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { EMPTY, catchError, finalize, forkJoin, map, Observable, of, Subject, takeUntil, tap, throwError } from 'rxjs'
import { Time } from '../domain/Time'
import { environment } from '../../environments/environment'
import { GBTimeZoneDatepickerI18n } from '../services/gb-time-zone-datepicker-i18n.service'
import { BookingAction, LoggingService } from '../services/logging.service'
import { VenueOpenDaysCalendar } from '../domain/VenueOpenDaysCalendar'
import { isVenueNotInServiceError } from '../services/venue-not-in-service-error'

@Component({
    selector: 'app-booking-modal-launcher',
    templateUrl: './booking-modal-launcher.component.html',
    providers: [
        {
            provide: NgbDatepickerI18n,
            useClass: GBTimeZoneDatepickerI18n,
        },
    ],
})
export class BookingModalLauncherComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string
    @Input({ transform: booleanAttribute }) noContext = false
    @Input() landingPageId?: string
    @Input({ transform: numberAttribute }) partySize?: number
    @Input({ transform: adaptStringToDate }) date?: Date
    @Input({ transform: adaptStringToDate }) idealTime?: Date
    @Input({
        transform: (value?: string) => value?.split(','),
    }) restrictedReasonIds?: string[]
    @Input() eventId?: string
    @Input() areaId?: string
    @Input({ transform: booleanAttribute }) autoOpen?: boolean
    @Input({ transform: booleanAttribute, alias: 'dm' }) darkMode = false
    isLoading = false
    venueColour?: string | null
    venueNotInService = false
    earliestDate: NgbDateStruct
    availability$!: Observable<Availability>
    form: FormGroup
    partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    openDaysCalendar = new VenueOpenDaysCalendar()
    isDateDisabled: (date: NgbDateStruct, current?: { year: number; month: number }) => boolean
    private ngbDateAdapter = new NgbDateNativeAdapter()
    protected ngbTimeAdapter = new NGBDateTimeAdapter()
    private onDestroy$ = new Subject<void>()

    get formDate(): Date | null {
        return this.ngbDateAdapter.toModel(this.form.get('date')!.value)
    }

    constructor(
        private availabilityService: AvailabilityService,
        private fb: FormBuilder,
        private loggingService: LoggingService
    ) {
        this.earliestDate = this.ngbDateAdapter.fromModel(new Date())!
        this.isDateDisabled = (date: NgbDateStruct) => {
            return this.openDaysCalendar.isDateClosed(date.year, date.month, date.day)
        }
        this.form = this.makeForm()
    }

    ngOnInit() {
        const today = new Date()
        this.loadInitialOpenDaysThenReloadAvailability(today)
        this.form.get('date')!.valueChanges
            .pipe(
                takeUntil(this.onDestroy$)
            )
            .subscribe(() => {
                this.reloadAvailability(false)
            })
    }

    ngAfterViewInit() {
        this.openBookingModalWithRouteContextIfProvidedOrRequestedTo()
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    dateSelected() {
        this.form.get('showDatePicker')!.setValue(false)
    }

    loadOpenDaysForMonth(dateInMonth: Date) {
        this.getOpenDaysForMonth(dateInMonth)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe()
    }

    private loadInitialOpenDaysThenReloadAvailability(initialDate: Date) {
        const nextMonthDate = new Date(initialDate.getFullYear(), initialDate.getMonth() + 1, 1)
        forkJoin([
            this.getOpenDaysForMonth(initialDate),
            this.getOpenDaysForMonth(nextMonthDate),
        ])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => {
                this.reloadAvailability(true)
            })
    }

    private getOpenDaysForMonth(dateInMonth: Date) {
        const year = dateInMonth.getFullYear()
        const oneIndexedMonth = dateInMonth.getMonth() + 1
        if (this.openDaysCalendar.isMonthAvailabilityKnown(year, oneIndexedMonth)) {
            return of(undefined)
        }
        this.openDaysCalendar.markMonthRequested(year, oneIndexedMonth)
        const startDate = new Date(year, dateInMonth.getMonth(), 1)
        const endDate = new Date(year, dateInMonth.getMonth() + 1, 0)
        return this.availabilityService.getOpenDays(this.organisationId, this.businessId, this.venueId, startDate, endDate)
            .pipe(
                tap(days => {
                    this.openDaysCalendar.addDays(days)
                }),
                map(() => undefined),
                catchError(() => {
                    this.openDaysCalendar.unmarkMonthRequested(year, oneIndexedMonth)
                    return of(undefined)
                })
            )
    }

    onDatepickerNavigate(event: { next: { year: number; month: number } }) {
        this.loadOpenDaysForMonth(new Date(event.next.year, event.next.month - 1, 1))
    }

    openBookingModal() {
        window.parent.postMessage({
            type: 'open-booking-modal',
            payload: {
                organisationId: this.organisationId,
                businessId: this.businessId,
                venueId: this.venueId,
                partySize: null,
                date: this.date,
                idealTime: this.idealTime,
                restrictedReasonIds: this.restrictedReasonIds,
                eventId: this.eventId,
                areaId: this.areaId
            },
        },
        '*')
    }

    openBookingModalWithContext(availability?: Availability) {
        if (this.form.invalid) {
            return
        }
        const partySize = this.form.get('partySize')!.value
        const date = this.ngbDateAdapter.toModel(this.form.get('date')!.value)
        const idealDateTime = new Date(this.form.get('idealTime')!.value)
        this.loggingService.trackAction(BookingAction.CheckAvailability, {
            partySize: partySize,
            date: date,
            idealTime: idealDateTime,
        })
        if (this.opensLandingPage(availability)) {
            this.openLandingPageInNewTab(availability)
            return
        }
        window.parent.postMessage({
            type: 'open-booking-modal',
            payload: {
                organisationId: this.organisationId,
                businessId: this.businessId,
                venueId: this.venueId,
                partySize: partySize,
                date: date,
                idealTime: idealDateTime,
                restrictedReasonIds: this.restrictedReasonIds,
                eventId: this.eventId,
                areaId: this.areaId
            },
        },
        '*')
    }

    private makeForm() {
        const today = new Date()
        const dateToday = this.ngbDateAdapter.fromModel(today)
        return this.fb.group({
            partySize: [null, [Validators.required, Validators.min(1)]],
            showDatePicker: [false],
            date: [dateToday, [Validators.required]],
            idealTime: [today.getTime(), [Validators.required]],
        })
    }

    private reloadAvailability(initialLoad: boolean) {
        this.availability$ = this.getAvailability()
        this.availability$
            .pipe(
                takeUntil(this.onDestroy$)
            )
            .subscribe(availability => {
                this.setPartySizeToDefaultReservablePartySize(availability)
                this.setDefaultIdealTimeToEarliestBookingSlotHourOrThisHour(availability)
                if (initialLoad) {
                    this.adjustDateIfNoBookingSlotsToday(availability)
                }
            })
    }

    private getAvailability() {
        const dateTime = this.ngbDateAdapter.toModel(this.form.get('date')!.value)
        if (!dateTime) {
            throw new Error('Date is null')
        }
        this.isLoading = true
        this.venueNotInService = false
        return this.availabilityService.getAvailability(
            this.organisationId,
            this.businessId,
            this.venueId,
            dateTime,
            dateTime,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            this.restrictedReasonIds
        )
            .pipe(
                finalize(() => this.isLoading = false),
                tap(availability => {
                    this.venueColour = availability.venue.primaryColour
                }),
                catchError(error => {
                    if (isVenueNotInServiceError(error)) {
                        this.venueNotInService = true
                        return EMPTY
                    }
                    return throwError(() => error)
                })
            )
    }

    private setPartySizeToDefaultReservablePartySize(availability: Availability) {
        if (this.form.get('partySize')?.value !== null) {
            return
        }
        this.form.get('partySize')?.setValue(availability.defaultReservablePartySize())
    }

    private setDefaultIdealTimeToEarliestBookingSlotHourOrThisHour(availability: Availability) {
        const earliestBookingSlot = availability.earliestAvailability()?.bookingSlot ?? null
        const time = earliestBookingSlot !== null ? new Date(earliestBookingSlot.dateTime) : new Date()
        const venueHasSpecificBookingSlots = availability.venue.hidesClosedBookingSlots
        const shouldAdjustInitialTimeToStartOfHour = !venueHasSpecificBookingSlots
        if (shouldAdjustInitialTimeToStartOfHour) {
            time.setMinutes(0, 0, 0)
        }
        this.form.get('idealTime')?.setValue(time.getTime())
    }

    private adjustDateIfNoBookingSlotsToday(availability: Availability) {
        const earliestBookingSlot = availability.earliestAvailability()
        const bookingSlotHasOptions = earliestBookingSlot && earliestBookingSlot.options.length > 0
        if (bookingSlotHasOptions) {
            return
        }
        const date = this.ngbDateAdapter.toModel(this.form.get('date')!.value)
        const today = new Date()
        const isDateToday = date && Helper.startOfDay(date).getTime() === Helper.startOfDay(today).getTime()
        if (!isDateToday) {
            return
        }
        this.form.get('date')!.setValue(this.ngbDateAdapter.fromModel(this.nextKnownOpenDateOrTomorrow(today)))
    }

    private nextKnownOpenDateOrTomorrow(date: Date): Date {
        const nextOpenDate = this.openDaysCalendar.firstOpenDateAfter(date)
        if (nextOpenDate) {
            return nextOpenDate
        }
        const tomorrow = new Date(date)
        tomorrow.setDate(tomorrow.getDate() + 1)
        return tomorrow
    }

    opensLandingPage(availability?: Availability): boolean {
        if (availability) {
            return !availability.venue.supportsModalExperience(this.landingPageId)
        }
        return this.landingPageId !== undefined
    }

    private openLandingPageInNewTab(availability?: Availability) {
        const url = this.makeLandingPageUrl(availability)
        if (!url) {
            return
        }
        window.open(url.toString(), '_blank')
    }

    private makeLandingPageUrl(availability?: Availability): URL | null {
        const path = this.landingPagePath(availability)
        if (!path) {
            return null
        }
        const url = new URL(
            path,
            this.landingPageId ? environment.landingPageBaseUrl : environment.venueStorefrontBaseUrl
        )
        const partySize = this.form.get('partySize')!.value
        const date = this.ngbDateAdapter.toModel(this.form.get('date')!.value)
        const idealDateTime = new Date(this.form.get('idealTime')!.value)
        const idealTime = new Time(null, idealDateTime.getHours(), idealDateTime.getMinutes())
        if (!partySize || !date || !idealTime) {
            return null
        }
        url.searchParams.append('partySize', partySize.toString())
        url.searchParams.append('date', Helper.makeLocalISOFormattedDateString(date))
        url.searchParams.append('idealTime', idealTime.toString())
        url.searchParams.append('ao', availability?.venue.shouldUseVenueStorefrontExperience() ? 'false' : 'true')
        if (this.restrictedReasonIds?.length) {
            url.searchParams.append('rrids', this.restrictedReasonIds.join(','))
        }
        if (this.eventId) {
            url.searchParams.append('eventId', this.eventId)
        }
        if (this.areaId) {
            url.searchParams.append('areaId', this.areaId)
        }
        return url
    }

    private landingPagePath(availability?: Availability): string | null {
        if (this.landingPageId) {
            return '/' + this.landingPageId
        }
        const venueSlug = availability?.venue.venueSlug
        return venueSlug ? '/discover/' + encodeURIComponent(venueSlug) : null
    }

    private openBookingModalWithRouteContextIfProvidedOrRequestedTo() {
        if (!this.partySize || !this.date || !this.idealTime) {
            if (this.autoOpen) {
                this.openBookingModal()
            }
            return
        }
        this.form.get('partySize')!.setValue(this.partySize)
        this.form.get('date')!.setValue(this.ngbDateAdapter.fromModel(this.date))
        const idealTime = new Time(null, this.idealTime.getHours(), this.idealTime.getMinutes())
        this.form.get('idealTime')!.setValue(idealTime.dateTimeOnDate(this.date))
        this.openBookingModalWithContext()
    }
}
